import { useState } from "react";
import { api, type OrgSettings } from "../api/client";
import { useT } from "../i18n";
import { Icons } from "./icons";

// Per-org default behaviors (§11). Currently the offline-cache default for new items; built to grow.
export function VaultSettingsDialog({
  orgId,
  orgName,
  initial,
  onClose,
  onSaved,
}: {
  orgId: string;
  orgName: string;
  initial: OrgSettings;
  onClose: () => void;
  onSaved: (s: OrgSettings) => void;
}) {
  const t = useT();
  const [offlineDefault, setOfflineDefault] = useState(initial.default_offline_allowed);
  const [brandingName, setBrandingName] = useState(initial.branding_name ?? "");
  const [brandingLogo, setBrandingLogo] = useState(initial.branding_logo ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = await api.putOrgSettings(orgId, {
        default_offline_allowed: offlineDefault,
        branding_name: brandingName.trim() || undefined,
        branding_logo: brandingLogo || undefined,
      });
      onSaved(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  // Read a chosen logo as a small data: URL (capped client-side; the server also rejects >256 KB).
  const onLogoFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200_000) {
      setError(t("Logo must be under 200 KB."));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setBrandingLogo(typeof reader.result === "string" ? reader.result : "");
    reader.readAsDataURL(file);
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60" onClick={onClose}>
      <div className="w-[480px] rounded-md border border-border bg-panel shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-border px-5 py-4 font-semibold">
          <Icons.settings className="h-4 w-4" /> {t("Vault defaults")} <span className="text-xs font-normal text-muted">· {orgName}</span>
        </div>
        <div className="flex flex-col gap-4 px-5 py-5 text-sm">
          <label className="flex items-start gap-3">
            <input type="checkbox" checked={offlineDefault} onChange={(e) => setOfflineDefault(e.target.checked)} className="mt-0.5" />
            <span>
              <span className="font-medium">{t("New items allowed in offline cache by default")}</span>
              <span className="block text-xs text-muted">{t("New items default to being available in the device offline cache (§15). Existing items are unchanged.")}</span>
            </span>
          </label>

          <div className="border-t border-border pt-4">
            <div className="mb-2 text-[11px] font-semibold tracking-wide text-muted">{t("BRANDING")}</div>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-muted">{t("Vault display name")}</span>
              <input
                value={brandingName}
                onChange={(e) => setBrandingName(e.target.value)}
                placeholder={orgName}
                className="rounded border border-border bg-panel2 px-2 py-1.5 outline-none focus:border-accent"
              />
            </label>
            <div className="mt-3 flex items-center gap-3">
              {brandingLogo ? (
                <img src={brandingLogo} alt="" className="h-10 w-10 rounded border border-border object-contain" />
              ) : (
                <div className="grid h-10 w-10 place-items-center rounded border border-border text-muted">—</div>
              )}
              <input type="file" accept="image/png,image/svg+xml,image/jpeg" onChange={onLogoFile} className="text-xs" />
              {brandingLogo && (
                <button onClick={() => setBrandingLogo("")} className="text-xs text-danger hover:underline">{t("Remove")}</button>
              )}
            </div>
            <p className="mt-1 text-xs text-muted">{t("Shown in the header for everyone in this vault. Non-secret.")}</p>
          </div>

          {error && <div className="text-xs text-danger">{error}</div>}
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="rounded border border-border px-3 py-1.5 text-xs hover:border-accent">{t("Cancel")}</button>
            <button onClick={save} disabled={busy} className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent2 disabled:opacity-60">
              {busy ? "…" : t("Save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
